import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import env from "@/app/config/env";
import { isDeactivatedAdminError } from "@/shared/api/errors";

function getAccessToken(): string | null {
  return localStorage.getItem("admin_access_token");
}

function setAccessToken(token: string): void {
  localStorage.setItem("admin_access_token", token);
}

function getRefreshToken(): string | null {
  return localStorage.getItem("admin_refresh_token");
}

function clearTokens(): void {
  localStorage.removeItem("admin_access_token");
  localStorage.removeItem("admin_refresh_token");
}

const LOGIN_PATH = "/login";
const DEACTIVATED_LOGIN_PATH = "/login?deactivated=1";
const LOGIN_REQUEST_URL = "/auth/login";
const REFRESH_REQUEST_URL = "/auth/refresh";

function redirectToLogin(deactivated: boolean): void {
  window.location.href = deactivated ? DEACTIVATED_LOGIN_PATH : LOGIN_PATH;
}

const baseURL = env.sameOriginApi ? "" : env.apiUrl;

type RetryableAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

export const http: AxiosInstance = axios.create({
  baseURL,
  headers: { Accept: "application/json" },
});

let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function drainQueue(token: string | null): void {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();

  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  return config;
});

http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    const axiosError = error as {
      config?: RetryableAxiosRequestConfig;
      response?: { status: number };
    };

    const status = axiosError.response?.status;
    const config = axiosError.config;

    if (isDeactivatedAdminError(error) && config?.url !== LOGIN_REQUEST_URL) {
      clearTokens();
      drainQueue(null);
      redirectToLogin(true);

      return Promise.reject(error);
    }

    if (status !== 401 || config?._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      clearTokens();

      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push((token) => {
          if (!token || !config) {
            reject(error);

            return;
          }

          const retryConfig: RetryableAxiosRequestConfig = { ...config, _retry: true };

          if (!retryConfig.headers) {
            retryConfig.headers = {};
          }

          retryConfig.headers["Authorization"] = `Bearer ${token}`;
          resolve(http(retryConfig));
        });
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post<{ accessToken: string; refreshToken?: string }>(
        REFRESH_REQUEST_URL,
        { refreshToken },
        {
          baseURL,
          headers: { "Content-Type": "application/json" },
        },
      );

      const newToken = res.data.accessToken;

      setAccessToken(newToken);

      if (res.data.refreshToken) {
        localStorage.setItem("admin_refresh_token", res.data.refreshToken);
      }

      drainQueue(newToken);

      if (!config) {
        return Promise.reject(error);
      }

      const retryConfig: RetryableAxiosRequestConfig = { ...config, _retry: true };

      if (!retryConfig.headers) {
        retryConfig.headers = {};
      }

      retryConfig.headers["Authorization"] = `Bearer ${newToken}`;

      return http(retryConfig);
    } catch (refreshError) {
      clearTokens();
      drainQueue(null);
      redirectToLogin(isDeactivatedAdminError(refreshError));

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export async function httpClient<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await http(config);

  return res.data;
}
